import { debounce } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AssetManager from "../../common/AssetManager";
import CustomSlider, { Mark } from "../../common/components/CustomSlider";
import FixedRangeSelect from "../../common/components/FixedRangeSelect";
import { Context as FormDataContext } from "../../context/formContext/FormContext";
import {
  PossibleTemperatureValue,
  TemperatureSelectType,
} from "../../context/formContext/types";
import { getAvailableSuggestables } from "../../context/suggestionContext/SuggestionContext";
import { CombinationException } from "../../context/suggestionContext/types";

const combinationExceptions: CombinationException[] = AssetManager.getAsset(
  "/clothing_exceptions_db.json"
)["exceptions"];

const TemperatureSelect = () => {
  const { state: formState, selectTemperature } = useContext(FormDataContext);
  const { temperatureSelectType, temperatureValue, activity } = formState;
  const { i18n } = useTranslation();
  const [minValue, setMinValue] = useState<number>(15);
  const [maxValue, setMaxValue] = useState<number>(25);
  const [middleMarks, setMiddleMarks] = useState<Mark[]>();

  useEffect(() => {
    return () => {};
  }, []);

  useEffect(() => {
    if (temperatureSelectType === TemperatureSelectType.SLIDER) {
      calcMiddleMarks();
    }

    return () => {};
  }, [temperatureSelectType, activity]);

  function calcMiddleMarks() {
    const possibleTemperatures: number[] = new Array<number>();

    let currentExceptions: CombinationException[] = [];

    const location = formState.activity?.location!;
    const humidity = 50;
    const ageGroup = formState.ageLocationPair?.ageGroup[0]!;
    const sweatComment =
      formState.activity?.sweatComment === null ||
      formState.activity?.sweatComment === undefined
        ? 0
        : formState.activity?.sweatComment;

    for (let temperature = minValue; temperature <= maxValue; temperature++) {
      currentExceptions = getAvailableSuggestables(
        combinationExceptions,
        location,
        temperature,
        humidity,
        ageGroup,
        sweatComment
      ) as CombinationException[];

      if (currentExceptions.length === 0) {
        possibleTemperatures.push(temperature);
      }
    }

    let possibleMin: number = possibleTemperatures[0];
    let possibleMax: number =
      possibleTemperatures[possibleTemperatures.length - 1];

    setMiddleMarks([
      { value: possibleMin, label: possibleMin + "°C" },
      { value: possibleMax, label: possibleMax + "°C" },
    ]);
  }

  function getAriaValueText(value: number) {
    return `${value}°C`;
  }

  const sliderDebounceCall = debounce((newValue: number) => {
    selectTemperature({
      type: TemperatureSelectType.SLIDER,
      temperature: newValue,
    });
  }, 500);

  return (
    <div className="temperature-select-container">
      <div className="temperature-select-header">
        <h5 className="input-label">{i18n.t("form_select_temperature")}</h5>

        {temperatureSelectType ? (
          <>
            {temperatureSelectType === TemperatureSelectType.SLIDER ? (
              <p className="input-description">
                Egy rövid magyarázó szöveg, hogy az outdoor skálán meddig
                ajánlunk ruhákat. Azon kívül miért nem.
              </p>
            ) : (
              <p className="input-description">
                Egy rövid magyarázó szöveg, hogy az indoor skálán miért csak
                bizonyos szeletek vannak megadva.
              </p>
            )}
          </>
        ) : (
          <p className="input-description">
            {i18n.t("first_fill_prev_sections")}
          </p>
        )}
      </div>

      {temperatureSelectType && (
        <>
          {temperatureSelectType === TemperatureSelectType.RANGE ? (
            <FixedRangeSelect
              ranges={activity!.fixedTemperatureRanges!}
              temperatureValue={temperatureValue}
              setTemperatureValue={(newValue: PossibleTemperatureValue) => {
                selectTemperature(newValue);
              }}
            ></FixedRangeSelect>
          ) : (
            <CustomSlider
              min={minValue}
              max={maxValue}
              defaultValue={20}
              step={1}
              marks={[
                { value: minValue, label: minValue + "°C" },
                ...(middleMarks ?? []),
                { value: maxValue, label: maxValue + "°C" },
              ]}
              getAriaValueText={getAriaValueText}
              debounceCall={sliderDebounceCall}
              temperatureValue={temperatureValue}
              setTemperatureValue={() => {}}
            ></CustomSlider>
          )}
        </>
      )}
    </div>
  );
};

export default TemperatureSelect;
